import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { resolveImageUrl } from "../components/ImageUploader";
import { BRAND } from "../lib/brand";
import { interestLabel } from "../lib/interests";
import {
    Package, MapPin, Compass, Clock, Route as RouteIcon, ImageIcon,
    FileText, Mountain, ArrowRight, Link2, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const packUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/adventures/${id}/trip-pack.zip`;

/**
 * Public, login-free "trip pack" landing page. Someone who receives a shared
 * link can preview the adventure and one-tap download the GPX + photos + brief
 * bundle — no account or beta code required.
 */
export default function PackPage() {
    const { id } = useParams();
    const [adv, setAdv] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const { data } = await api.get(`/adventures/${id}`);
                if (!cancel) setAdv(data);
            } catch {
                if (!cancel) setError(true);
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => { cancel = true; };
    }, [id]);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/share/pack/${id}`);
            toast.success("Share link copied");
        } catch {
            toast.error("Couldn't copy link");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-alpine-mist">
                <Loader2 className="w-8 h-8 text-ember-orange animate-spin" />
            </div>
        );
    }

    if (error || !adv) {
        return (
            <div data-testid="pack-not-found" className="min-h-screen flex flex-col items-center justify-center bg-alpine-mist text-center px-6">
                <Package className="w-12 h-12 text-slate-gray mb-4" />
                <h1 className="text-2xl font-black text-midnight-pine mb-2">Trip pack not found</h1>
                <p className="text-sm text-slate-gray mb-6">This guide may have been removed or set to private.</p>
                <Link to="/" className="px-5 py-2.5 rounded-full bg-midnight-pine text-white font-bold text-sm">
                    Explore {BRAND.name}
                </Link>
            </div>
        );
    }

    const cover = resolveImageUrl(adv.cover_photo || (adv.photos || [])[0]);
    const photoCount = (adv.photos || []).length;
    const hasRoute = (adv.route || []).length > 0;

    return (
        <div data-testid="pack-page" className="min-h-screen bg-alpine-mist">
            {/* Header */}
            <header className="border-b border-ridge-line bg-snowcap">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-baseline gap-1.5" data-testid="pack-brand">
                        <span className="font-display font-black text-lg text-midnight-pine">{BRAND.nameWordOne}</span>
                        <span className="font-display font-black text-lg text-ember-orange">{BRAND.nameWordTwo}</span>
                    </Link>
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ember-orange">
                        <Package className="w-4 h-4" /> Trip pack
                    </span>
                </div>
            </header>

            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
                <div className="rounded-2xl border border-ridge-line bg-snowcap overflow-hidden">
                    {/* Cover */}
                    <div className="relative aspect-[16/9] bg-trail-tan">
                        {cover ? (
                            <img src={cover} alt={adv.title} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center">
                                <Mountain className="w-10 h-10 text-midnight-pine/40" />
                            </div>
                        )}
                    </div>

                    <div className="p-5 sm:p-7">
                        <h1 className="text-2xl sm:text-3xl font-black text-midnight-pine leading-tight">{adv.title}</h1>
                        {adv.author?.name && (
                            <p className="mt-1 text-sm text-slate-gray">by {adv.author.name}</p>
                        )}

                        {/* Meta chips */}
                        <div className="mt-4 flex flex-wrap gap-2">
                            {adv.activity && <Chip icon={Compass}>{interestLabel(adv.activity)}</Chip>}
                            {adv.difficulty && <Chip icon={Mountain}>{adv.difficulty}</Chip>}
                            {(adv.location_name || adv.general_area) && <Chip icon={MapPin}>{adv.location_name || adv.general_area}</Chip>}
                            {adv.distance_km ? <Chip icon={RouteIcon}>{adv.distance_km} km</Chip> : null}
                            {adv.duration_hours ? <Chip icon={Clock}>{adv.duration_hours} hrs</Chip> : null}
                        </div>

                        {adv.summary && (
                            <p className="mt-4 text-sm text-granite leading-relaxed line-clamp-4">{adv.summary}</p>
                        )}

                        {/* What's inside */}
                        <div className="mt-6 rounded-xl border border-ridge-line bg-alpine-mist/60 p-4">
                            <p className="text-xs font-bold uppercase tracking-wider text-slate-gray mb-3">What's in this pack</p>
                            <ul className="space-y-2 text-sm text-granite">
                                <li className="flex items-center gap-2"><FileText className="w-4 h-4 text-midnight-pine" /> Trail brief (stats + what to know before you go)</li>
                                {hasRoute && <li className="flex items-center gap-2"><RouteIcon className="w-4 h-4 text-midnight-pine" /> GPS route (.gpx) — {adv.route.length} trackpoints</li>}
                                {photoCount > 0 && <li className="flex items-center gap-2"><ImageIcon className="w-4 h-4 text-midnight-pine" /> {photoCount} photo{photoCount === 1 ? "" : "s"}</li>}
                            </ul>
                        </div>

                        {/* Actions */}
                        <div className="mt-6 flex flex-col sm:flex-row gap-3">
                            <a
                                href={packUrl(adv.id)}
                                data-testid="pack-download"
                                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-ember-orange text-white font-bold hover:bg-ember-dark transition-all"
                            >
                                <Package className="w-5 h-5" /> Download trip pack
                            </a>
                            <Link
                                to={`/adventures/${adv.id}`}
                                data-testid="pack-open-guide"
                                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-ridge-line text-midnight-pine font-bold hover:border-midnight-pine transition-all"
                            >
                                Open full guide <ArrowRight className="w-4 h-4" />
                            </Link>
                            <button
                                onClick={copyLink}
                                data-testid="pack-copy-link"
                                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-ridge-line text-slate-gray font-bold hover:border-midnight-pine hover:text-midnight-pine transition-all sm:ml-auto"
                            >
                                <Link2 className="w-4 h-4" /> Copy link
                            </button>
                        </div>
                    </div>
                </div>

                <p className="mt-6 text-center text-xs text-slate-gray">
                    Carry it offline. Go do it. — {BRAND.name}
                </p>
            </div>
        </div>
    );
}

function Chip({ icon: Icon, children }) {
    return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-alpine-mist border border-ridge-line text-xs font-semibold text-granite capitalize">
            <Icon className="w-3.5 h-3.5 text-slate-gray" /> {children}
        </span>
    );
}
